import React, { useMemo, useState } from "react";
import Axios from "axios";
import { Grid, Typography, Button, TextField, Alert, Snackbar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

function PasswordReset() {
    const { t, i18n } = useTranslation();
    const { config, environment } = require("../../index");
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);

    const [successOpen, setSuccessOpen] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const resetPasswordEndpoint = useMemo(
        () => config?.[environment]?.resetPasswordEndpoint,
        [config, environment]
    );

    const endpointReady = typeof resetPasswordEndpoint === "string" && resetPasswordEndpoint.length > 0;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg("");

        if (!endpointReady) {
            setErrorMsg(t('Administration.EndpointError', "A jelszó-visszaállítás jelenleg nem elérhető (hiányzó backend endpoint)."));
            return;
        }

        if (!email.trim()) {
            setErrorMsg(t('Administration.EmailRequired', "Kérlek add meg az email címet."));
            return;
        }

        setSending(true);
        try {
            // Djoser: POST /api-auth-djoser/users/reset_password/  body: { email }
            await Axios.post(resetPasswordEndpoint, { email: email.trim() });

            // Djoser always returns 204 No Content for security
            setSuccessOpen(true);
            setEmail(""); // Clear the field
        } catch (err) {
            console.error("[PasswordReset] Error:", err);
            const status = err?.response?.status;

            if (status === 429) {
                setErrorMsg(t('Administration.TooManyAttempts', "Túl sok próbálkozás. Kérlek próbáld később."));
            } else if (status === 400) {
                const detail = err?.response?.data?.email?.[0] || t('Administration.EmailError', "Érvénytelen email cím.");
                setErrorMsg(detail);
            } else {
                setErrorMsg(t('Administration.GenericError', "Nem sikerült elküldeni a visszaállítási emailt. Próbáld újra később."));
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <Grid container justifyContent="center" sx={{ mt: 5, marginTop: { xs: '20px', md: '50px', lg: '100px' } }}>
            <Grid item xs={11} sm={10} md={8} lg={6} sx={{ border: "2px solid black", p: { xs: 2, md: 3 } }}>
                <Grid container justifyContent="center">
                    <Typography variant="h4" sx={{ textAlign: 'center', fontSize: { xs: '1.5rem', md: '2.125rem' } }}>{t('Administration.PasswordResetTitle', "JELSZÓ VISSZAÁLLÍTÁSA")}</Typography>
                </Grid>

                <Grid container sx={{ mt: 2 }}>
                    <Typography variant="body1" align="center">
                        {t('Administration.PasswordResetMessage', "Add meg az email címed, és küldünk egy linket a jelszó visszaállításához.")}
                    </Typography>
                </Grid>

                {!endpointReady && (
                    <Grid container sx={{ mt: 2 }}>
                        <Alert severity="info" sx={{ width: "100%" }}>
                            {t('Administration.EndpointMissing', "A funkció előkészítve, de a backend endpoint még nincs bekötve.")}
                        </Alert>
                    </Grid>
                )}

                {!!errorMsg && (
                    <Grid container sx={{ mt: 2 }}>
                        <Alert severity="error" sx={{ width: "100%" }}>
                            {errorMsg}
                        </Alert>
                    </Grid>
                )}

                <form onSubmit={handleSubmit}>
                    <Grid container sx={{ mt: 2 }}>
                        <TextField
                            label={t('Administration.Email', "Email")}
                            type="email"
                            variant="outlined"
                            fullWidth
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={sending}
                            required
                        />
                    </Grid>

                    <Grid container sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            type="submit"
                            disabled={!endpointReady || sending}
                        >
                            {sending ? t('Administration.Sending', "KÜLDÉS...") : t('Administration.RequestReset', "VISSZAÁLLÍTÁS KÉRÉSE")}
                        </Button>
                    </Grid>
                </form>

                <Grid container sx={{ mt: 2 }}>
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => navigate(`/${i18n.language}/login`)}
                        disabled={sending}
                    >
                        {t('Administration.BackToLogin')}
                    </Button>
                </Grid>

                <Snackbar
                    open={successOpen}
                    autoHideDuration={6000}
                    onClose={() => setSuccessOpen(false)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert onClose={() => setSuccessOpen(false)} severity="success" sx={{ width: "100%" }}>
                        {t('Administration.ResetEmailSent', "Ha az email cím regisztrálva van, hamarosan kapsz egy jelszó-visszaállítási linket az email fiókodba.")}
                    </Alert>
                </Snackbar>
            </Grid>
        </Grid>
    );
}

export default PasswordReset;